<template>
  <div id="container">
    <div id="title-panel">
      <div id="title-container">
        <h1>{{ $t('projectsList.projects') }}</h1>
      </div>
      <p id="description">
        {{ $t('projectsList.description') }}
      </p>
      <vf-button
        v-if="isUserRole([Role.Admin])"
        id="add-project-button"
        :size="buttonSizes.large"
        :variant="buttonVariants.secondary"
        @click="handleNewProjectClick"
      >
        {{ $t('projectsList.addProject') }}
      </vf-button>
    </div>

    <table-wrapper
      v-model:page-options="pageOptions"
      v-model:filters="filters"
      :fields="tableFields"
      :filter-configuration="filterConfiguration"
      :items="projects"
      :loading="isLoading"
      :mode="TableMode.CLIENT_SIDE"
      row-clickable
      @row-click="handleRowClick"
    >
      <template #cell(projectTitle)="{ data }">
        {{ data?.projectTitle }}
      </template>
    </table-wrapper>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue';
  import { IProject, Role, WebTour } from 'types';
  import axios from 'axios';
  import endpointsList from '../../helpers/endpointsList';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '@/composables/useToast';
  import { useRoute, useRouter } from 'vue-router';
  import {
    TableFieldsBuilder,
    TableFiltersBuilder,
    TableMode,
    useTableWrapper,
  } from '@/components/TableWrapper';
  import { Route, routeParams } from '@/routes';
  import { useTour } from '@/composables/useTour';

  const { t } = useI18n();
  const router = useRouter();
  const { createErrorToast } = useToast();
  const { startTour } = useTour();
  const route = useRoute();

  const projects = ref<IProject[]>([]);
  const { pageOptions, filters, isLoading } = useTableWrapper<IProject>(
    TableMode.CLIENT_SIDE,
    'projectTitle',
    getProjects,
  );
  const filterConfiguration = new TableFiltersBuilder<IProject>({
    translationKey: 'projectsList',
  })
    .addInputFilter('projectTitle', {
      width: '280px',
    })
    .addInputFilter('projectNumber', {
      width: '208px',
      translationKey: 'projectsList',
    })
    .addSelectFilter('projectStatus', {
      width: '120px',
    })
    .build();
  const tableFields = computed(() =>
    new TableFieldsBuilder<IProject>({
      sortable: true,
      labelTranslationKey: 'projectsList',
    })
      .addField('projectTitle', {
        width: '50%',
      })
      .addField('projectNumber', {
        width: '35%',
      })
      .addField('projectStatus', {
        width: '15%',
        valueTranslationKey: 'projectsList',
      })
      .build(),
  );
  async function getProjects() {
    try {
      const response = await axios.get<IProject[]>(
        endpointsList.projects.getAllProjects,
      );
      projects.value = response.data;
    } catch {
      createErrorToast(t('projectsList.errors.couldNotLoadProjects'));
    }
  }
  function handleRowClick(project: IProject) {
    router.push({
      name: Route.PROJECT_DETAILS,
      params: {
        [routeParams[Route.PROJECT_DETAILS].projectId]: project._id.toString(),
      },
    });
  }

  function handleNewProjectClick() {
    router.push({
      name: Route.CREATE_PROJECT,
    });
  }

  onMounted(() => {
    const key = route.meta.tourKey as WebTour;
    startTour(key);
  });
</script>

<style lang="scss" scoped>
  #container {
    #title-panel {
      margin-bottom: 50px;
      width: 100%;
      display: grid;
      grid-template-columns: 1fr 2fr 1fr;
      align-items: flex-start;
      justify-items: center;

      #title-container {
        justify-self: flex-start;
        display: flex;
        align-items: flex-start;

        h1 {
          font-size: 50px;
          font-weight: 700;
        }
      }

      p#description {
        margin-bottom: 0;
        max-height: 62px;
        width: 645px;
        font-size: 16px;
        line-height: 28px;
        font-weight: 500;
      }

      .add-project-button {
        justify-self: end;
      }
    }
  }
</style>
